import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  History,
  X,
  AlertTriangle,
  Activity,
  Pill,
  ClipboardList,
  ArrowRight,
  Eye,
  Download,
  ExternalLink,
} from 'lucide-react';
import {
  EPISODE_STATUS_LABELS,
  EPISODE_STATUS_VARIANTS,
  EpisodeStatusSchema,
  listProcedureNames,
  type EpisodeStatus,
  type Patient,
  type ProcedureListItem,
  type ProcedureType,
  type UserId,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { avatarGradient } from '@/components/ui/avatar';
import { usePageTitle } from '@/shared/store/page';
import { ageFromBirthdate, initials, formatShortDateEs } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { downloadAuthenticated, fetchBlob } from '@/shared/api/client';
import { useToast } from '@/shared/toast/toast-context';
import { usePatientsList } from '@/features/patients/api';
import { useProceduresList } from '@/features/procedures/api';
import { useUsersList } from '@/features/users/api';
import { useResultsByProcedure, resultDownloadUrl } from '@/features/results/api';

interface Preview {
  url: string;
  tipo: string;
  nombre: string;
}

interface Filters {
  desde: string;
  hasta: string;
  estado: EpisodeStatus | '';
  tipo: ProcedureType | '';
  medicoId: UserId | '';
}

const EMPTY_FILTERS: Filters = { desde: '', hasta: '', estado: '', tipo: '', medicoId: '' };

export function ConsultaHistoricaPage(): JSX.Element {
  usePageTitle('Consulta histórica', 'Gestión · Historial clínico transversal');

  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Patient | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [preview, setPreview] = useState<Preview | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setQ(qInput), 300);
    return () => window.clearTimeout(t);
  }, [qInput]);

  const searchQuery = usePatientsList({ page: 1, pageSize: 8, q });
  const matches = q.trim().length >= 2 ? (searchQuery.data?.items ?? []) : [];

  const openPreview = async (path: string, tipo: string, nombre: string): Promise<void> => {
    try {
      const blob = await fetchBlob(path);
      setPreview({ url: URL.createObjectURL(blob), tipo, nombre });
    } catch {
      // el modal de error lo maneja el toast en los handlers que lo usan
      setPreview(null);
      throw new Error('preview');
    }
  };

  const closePreview = (): void => {
    setPreview((p) => {
      if (p !== null) URL.revokeObjectURL(p.url);
      return null;
    });
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Buscador */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="w-4 h-4 text-primary" /> Consulta histórica
            </CardTitle>
            <CardDescription>
              Busca un paciente para ver el historial completo de sus episodios, documentos
              firmados y resultados.
            </CardDescription>
          </div>
        </CardHeader>
        <div className="p-6 pt-4">
          {selected === null ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <Input
                  autoFocus
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Nombre o documento del paciente…"
                  className="pl-9"
                />
              </div>
              {q.trim().length >= 2 && (
                <div className="mt-3 border border-border rounded-md divide-y divide-border overflow-hidden">
                  {searchQuery.isLoading ? (
                    <div className="px-4 py-3 text-text-muted text-sm">Buscando…</div>
                  ) : matches.length === 0 ? (
                    <div className="px-4 py-3 text-text-muted text-sm">
                      Sin coincidencias para “{q.trim()}”.
                    </div>
                  ) : (
                    matches.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelected(p)}
                        className="w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-surface-alt"
                      >
                        <div
                          className={cn(
                            'w-9 h-9 rounded-md grid place-items-center text-white text-xs font-bold flex-shrink-0',
                            avatarGradient(p.id),
                          )}
                        >
                          {initials(p.nombres, p.apellidos)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">
                            {p.nombres} {p.apellidos}
                          </div>
                          <div className="text-xs text-text-muted">
                            {p.tipoDoc} {p.documento}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-text-muted" />
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          ) : (
            <PatientHeader patient={selected} onClear={() => setSelected(null)} />
          )}
        </div>
      </Card>

      {selected !== null && (
        <>
          <HistoryFilters filters={filters} onChange={setFilters} />
          <PatientTimeline
            patientId={selected.id}
            filters={filters}
            onPreview={openPreview}
            onDownload={(path, name) => void downloadAuthenticated(path, name)}
          />
        </>
      )}

      {preview !== null && <PreviewModal preview={preview} onClose={closePreview} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Patient header                                                             */
/* -------------------------------------------------------------------------- */

function PatientHeader({
  patient,
  onClear,
}: {
  patient: Patient;
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="flex items-start gap-4">
      <div
        className={cn(
          'w-12 h-12 rounded-lg grid place-items-center text-white font-bold flex-shrink-0',
          avatarGradient(patient.id),
        )}
      >
        {initials(patient.nombres, patient.apellidos)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg font-bold">
            {patient.nombres} {patient.apellidos}
          </span>
          <Badge variant="indigo">{patient.id}</Badge>
        </div>
        <div className="text-xs text-text-muted mt-0.5">
          {patient.tipoDoc} {patient.documento} · {ageFromBirthdate(patient.fechaNacimiento)} años ·{' '}
          {patient.sexo === 'F' ? 'Femenino' : patient.sexo === 'M' ? 'Masculino' : 'Otro'}
          {patient.eps !== undefined && patient.eps !== '' ? ` · ${patient.eps}` : ''}
        </div>
        {patient.alergias !== undefined && patient.alergias !== '' && (
          <div className="mt-2">
            <Badge variant="red">
              <AlertTriangle className="w-3.5 h-3.5" /> Alergias: {patient.alergias}
            </Badge>
          </div>
        )}
      </div>
      <Button variant="secondary" size="sm" onClick={onClear}>
        <X className="w-4 h-4" /> Cambiar paciente
      </Button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Filters                                                                    */
/* -------------------------------------------------------------------------- */

function HistoryFilters({
  filters,
  onChange,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
}): JSX.Element {
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });
  const medicos = (usersQuery.data?.items ?? []).filter((u) => u.rol === 'medico');
  const tipos = useMemo(() => listProcedureNames(), []);
  const set = (patch: Partial<Filters>): void => onChange({ ...filters, ...patch });
  const hasFilters =
    filters.desde !== '' ||
    filters.hasta !== '' ||
    filters.estado !== '' ||
    filters.tipo !== '' ||
    filters.medicoId !== '';

  const selectCls =
    'h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

  return (
    <Card>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="desde">Desde</Label>
          <Input
            id="desde"
            type="date"
            value={filters.desde}
            onChange={(e) => set({ desde: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hasta">Hasta</Label>
          <Input
            id="hasta"
            type="date"
            value={filters.hasta}
            onChange={(e) => set({ hasta: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Estado</Label>
          <select
            value={filters.estado}
            onChange={(e) => set({ estado: e.target.value as EpisodeStatus | '' })}
            className={selectCls}
          >
            <option value="">Todos</option>
            {EpisodeStatusSchema.options.map((s) => (
              <option key={s} value={s}>
                {EPISODE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Tipo</Label>
          <SearchableSelect
            value={filters.tipo}
            onChange={(v) => set({ tipo: v as ProcedureType | '' })}
            placeholder="Todos"
            clearable
            options={tipos.map((t) => ({ value: t, label: t }))}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Médico</Label>
          <SearchableSelect
            value={filters.medicoId}
            onChange={(v) => set({ medicoId: v as UserId | '' })}
            placeholder="Todos"
            clearable
            options={medicos.map((m) => ({ value: m.id, label: m.nombre }))}
          />
        </div>
        {hasFilters && (
          <div className="xl:col-span-5">
            <Button size="sm" variant="ghost" onClick={() => onChange(EMPTY_FILTERS)}>
              <X className="w-4 h-4" /> Limpiar filtros
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Timeline                                                                   */
/* -------------------------------------------------------------------------- */

interface DocActions {
  onPreview: (path: string, tipo: string, nombre: string) => Promise<void>;
  onDownload: (path: string, nombre: string) => void;
}

function PatientTimeline({
  patientId,
  filters,
  onPreview,
  onDownload,
}: { patientId: Patient['id']; filters: Filters } & DocActions): JSX.Element {
  const query = useProceduresList({
    page: 1,
    pageSize: 100,
    pacienteId: patientId,
    ...(filters.desde !== '' ? { desde: filters.desde } : {}),
    ...(filters.hasta !== '' ? { hasta: filters.hasta } : {}),
    ...(filters.estado !== '' ? { estado: filters.estado } : {}),
    ...(filters.tipo !== '' ? { tipo: filters.tipo } : {}),
    ...(filters.medicoId !== '' ? { medicoId: filters.medicoId } : {}),
  });

  const episodes = query.data?.items ?? [];

  if (query.isLoading) {
    return (
      <Card>
        <div className="p-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 rounded bg-surface-alt animate-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  if (episodes.length === 0) {
    return (
      <Card>
        <div className="p-12 text-center text-text-muted text-sm flex flex-col items-center gap-2">
          <History className="w-8 h-8 opacity-50" />
          No hay episodios que coincidan con los filtros para este paciente.
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs text-text-muted px-1">
        {episodes.length} episodio{episodes.length === 1 ? '' : 's'} en el historial
      </div>
      {episodes.map((ep) => (
        <EpisodeCard key={ep.id} ep={ep} onPreview={onPreview} onDownload={onDownload} />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Episode card                                                               */
/* -------------------------------------------------------------------------- */

function EpisodeCard({
  ep,
  onPreview,
  onDownload,
}: { ep: ProcedureListItem } & DocActions): JSX.Element {
  const { showToast } = useToast();
  const resultsQuery = useResultsByProcedure(ep.id);
  const resultados = resultsQuery.data?.items ?? [];

  const safePreview = (path: string, tipo: string, nombre: string): void => {
    void onPreview(path, tipo, nombre).catch(() =>
      showToast({ kind: 'danger', title: 'No se pudo abrir el archivo' }),
    );
  };

  const consentPdf = `/api/consent/by-procedure/${encodeURIComponent(ep.id)}/pdf`;

  return (
    <Card>
      <div className="p-5">
        {/* Header del episodio */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-text-muted font-semibold">
              {formatShortDateEs(ep.fecha)} · {ep.hora}
            </div>
            <div className="text-base font-bold text-text mt-0.5">{ep.tipo}</div>
            {ep.tiposAdicionales !== undefined &&
              ep.tiposAdicionales.map((t) => (
                <div key={t} className="text-xs text-text-muted">
                  + {t}
                </div>
              ))}
            <div className="text-xs text-text-muted mt-1">
              <code className="font-mono">{ep.id}</code> · {ep.medicoNombre}
              {ep.entidad !== undefined && ep.entidad !== '' ? ` · ${ep.entidad}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={EPISODE_STATUS_VARIANTS[ep.estado]}>
              {EPISODE_STATUS_LABELS[ep.estado]}
            </Badge>
            <Button size="sm" variant="ghost" asChild>
              <Link to={`/procedimientos/${ep.id}`}>
                Abrir <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Documentos firmados */}
        <div className="mt-4 flex flex-wrap gap-2">
          {ep.docs.consentimiento === 'firmado' ? (
            <DocChip
              icon={<ClipboardList className="w-3.5 h-3.5" />}
              label="Consentimiento"
              onView={() =>
                safePreview(consentPdf, 'pdf', `consentimiento-${ep.id}.pdf`)
              }
              onDownload={() => onDownload(consentPdf, `consentimiento-${ep.id}.pdf`)}
            />
          ) : null}
          {ep.docs.anestesia === 'firmado' ? (
            <DocChipLink
              icon={<Activity className="w-3.5 h-3.5" />}
              label="Registro de anestesia"
              to={`/anestesia/${ep.id}`}
            />
          ) : null}
          {ep.docs.gasto === 'firmado' ? (
            <DocChipLink
              icon={<Pill className="w-3.5 h-3.5" />}
              label="Hoja de gasto"
              to={`/gasto/${ep.id}`}
            />
          ) : null}
          {ep.docs.consentimiento !== 'firmado' &&
            ep.docs.anestesia !== 'firmado' &&
            ep.docs.gasto !== 'firmado' && (
              <span className="text-xs text-text-muted">Sin documentos firmados.</span>
            )}
        </div>

        {/* Resultados */}
        {resultados.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted mb-2">
              Resultados ({resultados.length})
            </div>
            <div className="flex flex-col gap-2">
              {resultados.map((r) => {
                const path = resultDownloadUrl(r.id);
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-md border border-border"
                  >
                    <span
                      className={cn(
                        'w-8 h-8 rounded grid place-items-center text-[10px] font-bold flex-shrink-0',
                        r.tipo === 'pdf'
                          ? 'bg-danger-soft text-red-700'
                          : 'bg-info-soft text-sky-700',
                      )}
                    >
                      {r.tipo === 'pdf' ? 'PDF' : 'IMG'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.nombreOriginal}</div>
                      <div className="text-[11px] text-text-muted">
                        {(r.tamanoBytes / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => safePreview(path, r.tipo, r.nombreOriginal)}
                    >
                      <Eye className="w-3.5 h-3.5" /> Ver
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDownload(path, r.nombreOriginal)}>
                      <Download className="w-3.5 h-3.5" /> Descargar
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function DocChip({
  icon,
  label,
  onView,
  onDownload,
}: {
  icon: JSX.Element;
  label: string;
  onView: () => void;
  onDownload: () => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
      <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-text bg-surface-alt">
        {icon} {label}
      </span>
      <button
        type="button"
        onClick={onView}
        className="px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5 border-l border-border"
      >
        Ver
      </button>
      <button
        type="button"
        onClick={onDownload}
        className="px-2.5 py-1.5 text-xs font-semibold text-text-muted hover:bg-surface-alt border-l border-border"
      >
        Descargar
      </button>
    </div>
  );
}

function DocChipLink({
  icon,
  label,
  to,
}: {
  icon: JSX.Element;
  label: string;
  to: string;
}): JSX.Element {
  return (
    <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
      <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-text bg-surface-alt">
        {icon} {label}
      </span>
      <Link
        to={to}
        className="px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5 border-l border-border"
      >
        Ver
      </Link>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Preview modal                                                              */
/* -------------------------------------------------------------------------- */

function PreviewModal({ preview, onClose }: { preview: Preview; onClose: () => void }): JSX.Element {
  const download = (): void => {
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = preview.nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" onClick={onClose}>
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-semibold text-sm text-slate-900 truncate">{preview.nombre}</div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" onClick={download}>
            <Download className="w-4 h-4" /> Descargar
          </Button>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <X className="w-4 h-4" /> Cerrar
          </Button>
        </div>
      </div>
      <div
        className="flex-1 min-h-0 overflow-auto bg-slate-800 grid place-items-center p-2"
        onClick={(e) => e.stopPropagation()}
      >
        {preview.tipo === 'pdf' ? (
          <iframe src={preview.url} title={preview.nombre} className="w-full h-full bg-white rounded" />
        ) : (
          <img
            src={preview.url}
            alt={preview.nombre}
            className="max-w-full max-h-full object-contain"
          />
        )}
      </div>
    </div>
  );
}
